import React, { useState } from 'react';

import { Box } from '@mui/material';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { FormattedMessage } from 'react-intl';

import { ITimelineItem } from '@clients/timelineClient';
import TimelineItemFactory from '@components/accountTimeline/accountTimelineBlock/TimelineItemFactory';
import LoadingError from '@components/loadingError/LoadingError';
import getEnumTranslation from '@utils/getEnumTranslation';

type AccountTimelinePanelProps = {
    onClick?: () => void;
    totalItemsToDisplay: number;
    isLoading?: boolean;
    isError?: boolean;
    panels?: ITimelineItem[];
};

const TimelinePanel = ({
    onClick,
    totalItemsToDisplay,
    isLoading = false,
    isError = false,
    panels = [],
}: AccountTimelinePanelProps) => {
    const [isHovered, setIsHovered] = useState(false);

    if (isError) {
        return (
            <Panel>
                <PanelContent>
                    <LoadingError />
                </PanelContent>
            </Panel>
        );
    }

    // if loading and no data, then create some dummy data to be populated for the skeleton
    if (isLoading) {
        panels = [];
        for (let i = 0; i <= totalItemsToDisplay; i++) {
            panels.push({
                account_id: 0,
                category: '',
                type: 'SKELETON',
                tenant_account_timestamp: new Date().toISOString().slice(0, 10),
                data: { item: {} },
                timestamp: i.toString(),
                correlation_id: '',
            });
        }
    }

    // cut the list down to the maximum items
    panels.splice(totalItemsToDisplay);

    // if we have 0 items, then create a No Timeline Items to show message
    if (panels?.length === 0) {
        panels.push({
            account_id: 0,
            category: '',
            type: 'NOTIMELINE',
            tenant_account_timestamp: new Date().toISOString().slice(0, 10),
            data: { item: {} },
            timestamp: new Date().toISOString().slice(0, 10),
            correlation_id: '',
        });
    }

    return (
        <Panel isHovered={setIsHovered} onClick={onClick}>
            <PanelTitle
                loading={isLoading}
                hovered={isHovered}
                arrowLabel={getEnumTranslation('viewAll')}>
                <FormattedMessage
                    id="AccountStatementsPanel.latestTimelineActivity"
                    defaultMessage="Latest timeline activity"
                />
            </PanelTitle>
            <PanelContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                    }}>
                    {panels.map((panel) => (
                        <TimelineItemFactory
                            key={panel.timestamp}
                            timelineItem={panel}
                            panelMode
                            highlightOnHover={false}
                            showTooltip
                        />
                    ))}
                </Box>
            </PanelContent>
        </Panel>
    );
};

export default TimelinePanel;
